import { Icon } from '@iconify/react'
import type { CSSProperties } from 'react'
import { lightBands, sunTimes, type Light, type LatLon } from '@jupi/shared'
import { formatTime } from '@jupi/ui'

// Translucent, so the "today" tint and the grid lines still show through, and
// readable on the light and the dark theme alike.
export const LIGHT_TINT: Record<Light, string> = {
  day: 'transparent',
  twilight: 'rgba(245, 158, 66, 0.14)',
  night: 'rgba(38, 50, 120, 0.22)',
}

// "↑ 07:12  ↓ 19:30" for one local calendar day (YYYY-MM-DD), or a word when
// the sun does not rise or set that day.
export function SunLine({ dayKey, place, labels }: { dayKey: string; place: LatLon; labels: { sunrise: string; sunset: string; polarNight: string; midnightSun: string } }) {
  const { sun } = sunTimes(dayKey, place.lat, place.lon)
  if (sun.state !== 'normal') {
    return <span>{sun.state === 'alwaysUp' ? labels.midnightSun : labels.polarNight}</span>
  }
  return (
    // Each time is its own unbreakable unit, so a narrow column wraps
    // between them instead of clipping.
    <span>
      <span style={{ whiteSpace: 'nowrap' }}>
        <Icon icon="mdi:weather-sunset-up" width={14} style={{ verticalAlign: -3 }} aria-label={labels.sunrise} />
        {formatTime(sun.rise!)}
      </span>{' '}
      <span style={{ whiteSpace: 'nowrap' }}>
        <Icon icon="mdi:weather-sunset-down" width={14} style={{ verticalAlign: -3 }} aria-label={labels.sunset} />
        {formatTime(sun.set!)}
      </span>
    </span>
  )
}

const SLOT_MS = 30 * 60_000

// Style for one 30-minute time-grid slot: the light across it as hard-stop
// gradient bands, and where night begins or ends inside it a dashed line and
// a "Night until 07:28" label (calendar.css draws both from the custom
// properties below).
export function slotDaylight(
  slotStart: Date,
  place: LatLon,
  label: { from: (time: string) => string; until: (time: string) => string },
): { className?: string; style: CSSProperties } {
  const from = slotStart.getTime()
  const bands = lightBands(from, from + SLOT_MS, place)
  const stops = bands
    .map((b) => `${LIGHT_TINT[b.light]} ${((b.from - from) / SLOT_MS) * 100}% ${((b.to - from) / SLOT_MS) * 100}%`)
    .join(', ')
  const style: Record<string, string> = { backgroundImage: bands.length === 1 ? `linear-gradient(${stops})` : `linear-gradient(to bottom, ${stops})` }
  // Night meeting non-night inside the slot is the edge to annotate.
  for (let i = 0; i < bands.length - 1; i++) {
    const a = bands[i]!
    const b = bands[i + 1]!
    if ((a.light === 'night') === (b.light === 'night')) continue
    const edge = new Date(b.from).toISOString()
    style['--daylight-edge'] = `${((b.from - from) / SLOT_MS) * 100}%`
    style['--daylight-label'] = JSON.stringify(a.light === 'night' ? label.until(formatTime(edge)) : label.from(formatTime(edge)))
    return { className: `daylight-edge daylight-edge-${a.light === 'night' ? 'until' : 'from'}`, style }
  }
  return { style }
}
